import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  View,
  Flex,
  Text,
  Button,
  TextField,
  TextArea,
  Picker,
  PickerItem,
  SearchField,
  RadioGroup,
  Radio,
  ProgressCircle,
} from "./Common/spectrumCompat";
import { displayToasts } from "../Helpers/displayAppToast";
import FullScreenLoader from "./Common/FullScreenLoader";
import DxpRegistrationTopHeader from "./DxpRegistrationTopHeader";
import WizardNavLayout from "./WizardNavLayout";
import { setPartnerRegistrationFormStep } from "../Redux/actions";
import {
  flattenWithSeparator,
  unflattenWithSeparator,
  getCommonAPIHeaders,
  getCompanyPartnerLevel,
  getStoredMembershipType,
  persistPartnerLevel,
} from "../Helpers/utils";
import {
  saveRegistrationForm,
  getAffiliatedCompany,
  getStates,
} from "../Services/PartnerRegistrationAPI";
import { PROGRAM_SPECIFIC_NAMES } from "../Helpers/constants";
import companyInfoConfig from "../ContentConfig/CompanyInfoStep";
import {
  validateCompanyInfo,
  validateCompanyInfoForSaveExit,
  clearCompanyValidationInlineErrors,
  hasAnyCompanyFieldInput,
  scheduleShowCompanyValidationErrorsBelowFields,
} from "../Helpers/companyValidationUtils";
import {
  DXP_FIELD_MAX_LENGTH,
  DXP_LINKEDIN_URL_VALIDATION_MESSAGE,
  applyDxpPersonalInfoRegisterDefaults,
  formatDialPrefixOnly,
  formatPhoneDialDisplay,
  validateCompanyWebsiteUrl,
  validateDxpPhoneLocalLine,
  validateDxpReferredByOptional,
  validateLinkedInProfileUrl,
  getDxpAdditionalInfoFieldError,
  DXP_COMPANY_NUMBER_OF_EMPLOYEES_OPTIONS,
  COMPANY_INFO_ADDRESS_STATE_PICKLIST_EMPTY,
} from "../Helpers/dxpRegistrationValidation";
import { DxpPhoneNumberField } from "./Common";

/* eslint-disable @typescript-eslint/no-explicit-any */
interface CountryItem {
  countrycode: string;
  country: string;
  defaultTimeZone?: string;
  phoneCode?: string;
}

/** `id` matches React Spectrum `Picker` `items` collection keys. */
type PhonePickerCountry = CountryItem & { id: string };

interface StateItem {
  stateCode: string;
  stateName: string;
}

interface FacetItem {
  ownership?: string[];
}

const primaryFooterPillLabelClass = "dxp-membership-primary-footer-pill";

const secondaryFooterPillLabelClass = "dxp-membership-secondary-footer-pill";

const companyStepFooterActionDisabledClass =
  "dxp-company-step-footer-action--disabled";

/** User profile lives in Redux when session was not updated; merge so /register gets personalInfo. */
function mergePersonalDraftIntoFlatForm(
  base: Record<string, any>,
  draft: Record<string, unknown> | null | undefined,
): Record<string, any> {
  const p = draft?.personalInfo;
  if (!p || typeof p !== "object") return base;
  const flat = flattenWithSeparator(
    { personalInfo: p as Record<string, unknown> },
    "_",
  );
  return { ...flat, ...base };
}

const PROGRAM = "DXP";

const DUPLICATE_COMPANY_NAME_MESSAGE =
  "Company name is already registered. Please use another name";

type CompanyFieldTouchKey =
  | "companyName"
  | "website"
  | "phone"
  | "linkedin"
  | "primaryBusiness"
  | "ownership"
  | "totalEmployees"
  | "referredBy"
  | "companyDescription"
  | "programBenefits"
  | "hqAddress"
  | "addressCountry"
  | "addressState"
  | "addressCity"
  | "addressZipCode"
  | "parentCompany";

export { mergePersonalDraftIntoFlatForm };

type CompanyInformationProps = {
  /** Dev preview: fixed standalone layout, no Redux step sync, safe back/next. */
  previewMode?: boolean;
};

function CompanyInformation({ previewMode = false }: CompanyInformationProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const registration = useSelector((state: any) => state?.registration || {});
  const selectedRegisterCompanyType = previewMode
    ? "standaloneCompany"
    : (registration?.selectedRegisterCompanyType ??
      localStorage.getItem("selectedRegisterCompanyType"));
  const accountId = localStorage.getItem("accountId");

  const isSubsidiary = selectedRegisterCompanyType === "subsidaryCompany";

  const [isLoading, setIsLoading] = useState(false);
  const [countries, setCountries] = useState<CountryItem[]>([]);
  const [states, setStates] = useState<StateItem[]>([]);
  /** False while `getStates(addressCountry)` is in flight (avoid treating [] as “no states”). */
  const [statesFetchSettled, setStatesFetchSettled] = useState(true);
  const [facets, setFacets] = useState<FacetItem>({ ownership: [] });

  const [parentCompanySearch, setParentCompanySearch] = useState("");
  const [parentCompanySuggestions, setParentCompanySuggestions] = useState<
    any[]
  >([]);
  const [parentCompanyLoading, setParentCompanyLoading] = useState(false);
  const [selectedParentCompany, setSelectedParentCompany] = useState<
    any | null
  >(null);
  const [showParentSuggestions, setShowParentSuggestions] = useState(false);
  const parentSearchDebounce = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const parentCompanySearchRef = useRef(parentCompanySearch);
  const selectedParentCompanyRef = useRef(selectedParentCompany);
  parentCompanySearchRef.current = parentCompanySearch;
  selectedParentCompanyRef.current = selectedParentCompany;

  const [addressChoice, setAddressChoice] = useState<
    "Parent Address" | "Partner Address"
  >("Parent Address");
  const addressChoiceRef = useRef(addressChoice);
  addressChoiceRef.current = addressChoice;

  const [narrowForAddressChoice, setNarrowForAddressChoice] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 640px)").matches,
  );

  const [companyName, setCompanyName] = useState("");
  const [website, setWebsite] = useState("");
  const [phoneDialCountryCode, setPhoneDialCountryCode] = useState("");
  const [phone, setPhone] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const pendingPhonePrefixRef = useRef<string | null>(null);
  /** ISO country from User profile phone picker — seed company phone dial when company phone has no saved prefix. */
  const profilePhoneDialIsoRef = useRef<string | null>(null);
  /** Persisted display name for state code when states list is not yet loaded (session round-trip). */
  const addressStateNameFallbackRef = useRef("");
  const [primaryBusiness, setPrimaryBusiness] = useState<string>("");
  const [ownership, setOwnership] = useState<string>("");
  const [totalEmployees, setTotalEmployees] = useState<string>("");
  const [referredBy, setReferredBy] = useState("");
  const [hqAddress, setHqAddress] = useState("");
  const [addressCountry, setAddressCountry] = useState("");
  const [addressState, setAddressState] = useState("");
  const [addressCity, setAddressCity] = useState("");
  const [addressZipCode, setAddressZipCode] = useState("");
  const [companyDescription, setCompanyDescription] = useState("");
  const [programBenefits, setProgramBenefits] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  /** Inline messages only for fields the user has interacted with or that contain data. */
  const [touchedFields, setTouchedFields] = useState<
    Partial<Record<CompanyFieldTouchKey, boolean>>
  >({});
  const [duplicateCompanyNameFromApi, setDuplicateCompanyNameFromApi] =
    useState(false);

  const markTouched = useCallback((key: CompanyFieldTouchKey) => {
    setTouchedFields((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  }, []);

  useLayoutEffect(() => {
    let parsedSession: Record<string, any> | null = null;
    let hasParentAddress = false;
    const persistedStr = sessionStorage.getItem("registrationFormData");
    if (persistedStr) {
      try {
        const p: Record<string, any> = JSON.parse(persistedStr);
        parsedSession = p;
        const sub = selectedRegisterCompanyType === "subsidaryCompany";
        if (
          sub &&
          p.companyInfo_addressChoice == null &&
          (String(p.companyInfo_address_hqAddress ?? "").trim() ||
            String(p.companyInfo_address_country ?? "").trim() ||
            String(p.companyInfo_address_city ?? "").trim() ||
            String(p.companyInfo_address_zipCode ?? "").trim())
        ) {
          p.companyInfo_addressChoice = "Partner Address";
        }
        if (p.companyInfo_companyName)
          setCompanyName(p.companyInfo_companyName);
        if (p.companyInfo_website) setWebsite(p.companyInfo_website);
        if (p.companyInfo_phone) {
          const raw = String(p.companyInfo_phone).trim();
          const idx = raw.indexOf("-");
          if (idx !== -1) {
            pendingPhonePrefixRef.current = raw.slice(0, idx).trim();
            setPhone(raw.slice(idx + 1).trim());
          } else {
            setPhone(raw);
          }
        }
        if (p.companyInfo_linkedin) setLinkedin(p.companyInfo_linkedin);
        if (p.companyInfo_primaryBusiness)
          setPrimaryBusiness(p.companyInfo_primaryBusiness);
        if (p.companyInfo_ownership) setOwnership(p.companyInfo_ownership);
        if (p.companyInfo_totalEmployees)
          setTotalEmployees(p.companyInfo_totalEmployees);
        if (p.companyInfo_referredBy) setReferredBy(p.companyInfo_referredBy);
        if (p.companyInfo_address_hqAddress)
          setHqAddress(p.companyInfo_address_hqAddress);
        if (p.companyInfo_address_country) {
          setAddressCountry(p.companyInfo_address_country);
          setStatesFetchSettled(false);
          setStates([]);
        }
        if (p.companyInfo_address_state)
          setAddressState(p.companyInfo_address_state);
        if (p.companyInfo_address_city)
          setAddressCity(p.companyInfo_address_city);
        if (p.companyInfo_address_zipCode)
          setAddressZipCode(p.companyInfo_address_zipCode);
        if (
          typeof p.companyInfo_address_stateName === "string" &&
          p.companyInfo_address_stateName.trim()
        ) {
          addressStateNameFallbackRef.current =
            p.companyInfo_address_stateName.trim();
        }
        if (p.companyInfo_companyDescription)
          setCompanyDescription(p.companyInfo_companyDescription);
        if (p.companyInfo_programBenefits)
          setProgramBenefits(p.companyInfo_programBenefits);
        if (p.companyInfo_addressChoice)
          setAddressChoice(p.companyInfo_addressChoice);
        const parentName =
          p.companyInfo_affiliatedCompany_name || p.affiliatedCompanyName;
        if (parentName) {
          setParentCompanySearch(parentName);
          const savedAddress = {
            hqAddress: p.companyInfo_address_hqAddress,
            country: p.companyInfo_address_country,
            countryName: p.companyInfo_address_countryName,
            state: p.companyInfo_address_state,
            stateName: p.companyInfo_address_stateName,
            city: p.companyInfo_address_city,
            zipCode: p.companyInfo_address_zipCode,
          };
          const hasSavedAddress = [
            savedAddress.hqAddress,
            savedAddress.country,
            savedAddress.state,
            savedAddress.city,
            savedAddress.zipCode,
          ].some((value) => String(value ?? "").trim() !== "");
          const affiliatedAddress =
            p.companyInfo_affiliatedCompany_hqAddress ||
            p.companyInfo_affiliatedCompany_country
              ? {
                  hqAddress: p.companyInfo_affiliatedCompany_hqAddress,
                  country: p.companyInfo_affiliatedCompany_country,
                  countryName: p.companyInfo_affiliatedCompany_countryName,
                  state: p.companyInfo_affiliatedCompany_state,
                  stateName: p.companyInfo_affiliatedCompany_stateName,
                  city: p.companyInfo_affiliatedCompany_city,
                  zipCode: p.companyInfo_affiliatedCompany_zipCode,
                }
              : undefined;
          const parentAddress =
            p.companyInfo_addressChoice === "Parent Address" && hasSavedAddress
              ? savedAddress
              : affiliatedAddress;
          hasParentAddress = !!parentAddress;
          setSelectedParentCompany({
            name: parentName,
            accountId: p.companyInfo_affiliatedCompany_accountId,
            id: p.companyInfo_affiliatedCompany_accountId,
            level: p.companyInfo_affiliatedCompany_level,
            address: parentAddress,
          });
        }
        const ppc = p.personalInfo_phoneCountryCode;
        if (typeof ppc === "string" && ppc.trim()) {
          profilePhoneDialIsoRef.current = ppc.trim();
        }
      } catch {
        parsedSession = null;
      }
    }
    if (!profilePhoneDialIsoRef.current) {
      const pi = registration?.dxpUserProfileDraft?.personalInfo;
      if (pi && typeof pi === "object") {
        const pc = (pi as Record<string, unknown>).phoneCountryCode;
        if (typeof pc === "string" && pc.trim()) {
          profilePhoneDialIsoRef.current = pc.trim();
        }
      }
    }
    if (isSubsidiary) {
      const p = parsedSession;
      const hasParentObject = !!(
        p?.companyInfo_affiliatedCompany_name || p?.affiliatedCompanyName
      );
      const hasPersistedSubsidiaryAddress =
        !!p &&
        (String(p.companyInfo_address_hqAddress ?? "").trim() ||
          String(p.companyInfo_address_country ?? "").trim() ||
          String(p.companyInfo_address_city ?? "").trim() ||
          String(p.companyInfo_address_zipCode ?? "").trim());
      const persistedChoice = p?.companyInfo_addressChoice;
      let effectiveChoice: "Parent Address" | "Partner Address";
      if (
        persistedChoice === "Partner Address" ||
        persistedChoice === "Parent Address"
      ) {
        effectiveChoice = persistedChoice;
      } else if (hasPersistedSubsidiaryAddress) {
        effectiveChoice = "Partner Address";
      } else {
        effectiveChoice = "Parent Address";
      }
      if (
        effectiveChoice === "Parent Address" &&
        (!hasParentObject || !hasParentAddress)
      ) {
        setAddressChoice("Partner Address");
        setHqAddress("");
        setAddressCountry("");
        setAddressState("");
        setAddressCity("");
        setAddressZipCode("");
      }
    }
    // Mount-only hydration; registration is snapshot from first paint.
    // useLayoutEffect: apply before paint; subsidiary guard uses parsed session
    // (not React state) so it does not race with React 17 passive-effect flushing.
  }, []);

  const apiUrl =
    window?.partnerRegistrationConfig?.REACT_APP_API_URL ||
    "https://partner-registration-dev.adobe.io";

  const fetchCountries = useCallback(async () => {
    const cached = sessionStorage.getItem("userProfileCountries");
    if (cached) {
      try {
        setCountries(JSON.parse(cached));
        return;
      } catch {
        sessionStorage.removeItem("userProfileCountries");
      }
    }
    try {
      const headers = getCommonAPIHeaders();
      const res = await fetch(`${apiUrl}/api/v1/DXP/country`, { headers });
      if (res.ok) {
        const data = await res.json();
        setCountries(data);
        sessionStorage.setItem("userProfileCountries", JSON.stringify(data));
      }
    } catch (err) {
      // eslint-disable-next-line no-console -- network diagnostics
      console.error("Failed to fetch countries", err);
    }
  }, [apiUrl]);

  useEffect(() => {
    if (countries.length === 0) return;

    const prefix = pendingPhonePrefixRef.current;
    if (prefix != null) {
      const normalized = prefix.replace(/\s/g, "");
      const match = countries.find(
        (c) =>
          c.countrycode === prefix ||
          (c.phoneCode && c.phoneCode.replace(/\s/g, "") === normalized),
      );
      if (match) {
        setPhoneDialCountryCode(match.countrycode);
        pendingPhonePrefixRef.current = null;
      }
      return;
    }

    const profileIso = profilePhoneDialIsoRef.current;
    if (!profileIso) return;
    const valid = countries.some((c) => c.countrycode === profileIso);
    if (!valid) return;
    setPhoneDialCountryCode((prev) => prev || profileIso);
  }, [countries]);

  /** Increments on each `getStates` call so stale responses are ignored. */
  const statesFetchIdRef = useRef(0);

  const fetchStates = useCallback(async (country: string) => {
    if (!country) {
      statesFetchIdRef.current += 1;
      setStates([]);
      setStatesFetchSettled(true);
      return;
    }
    statesFetchIdRef.current += 1;
    const reqId = statesFetchIdRef.current;
    setStatesFetchSettled(false);
    setStates([]);
    try {
      const resp: any = await getStates(country);
      let next: StateItem[] = [];
      if (resp?.status === 1 && resp?.response?.state) {
        next = resp.response.state.map((s: any) => ({
          stateCode: s.stateCode,
          stateName: s.stateName,
        }));
      }
      if (reqId !== statesFetchIdRef.current) return;
      setStates(next);
      if (next.length === 0) {
        setAddressState("");
        addressStateNameFallbackRef.current = "";
      }
    } catch {
      if (reqId !== statesFetchIdRef.current) return;
      setStates([]);
      setAddressState("");
      addressStateNameFallbackRef.current = "";
    } finally {
      if (reqId === statesFetchIdRef.current) {
        setStatesFetchSettled(true);
      }
    }
  }, []);

  const fetchFacets = useCallback(async () => {
    const cached = sessionStorage.getItem("userProfileFacets");
    if (cached) {
      try {
        const f = JSON.parse(cached) as { ownership?: string[] };
        if (Array.isArray(f.ownership) && f.ownership.length > 0) {
          setFacets({ ownership: f.ownership });
          return;
        }
        /* User profile step caches facets without ownership — must load from API. */
      } catch {
        sessionStorage.removeItem("userProfileFacets");
      }
    }
    try {
      const headers = getCommonAPIHeaders();
      const res = await fetch(`${apiUrl}/api/v1/DXP/partner/facet`, {
        headers,
      });
      if (res.ok) {
        const data = await res.json();
        const ownershipList = data.ownership || [];
        setFacets({ ownership: ownershipList });
        let parsed: Record<string, unknown> = {};
        try {
          const existing = sessionStorage.getItem("userProfileFacets");
          if (existing) parsed = JSON.parse(existing);
        } catch {
          parsed = {};
        }
        sessionStorage.setItem(
          "userProfileFacets",
          JSON.stringify({ ...parsed, ownership: ownershipList }),
        );
      }
    } catch (err) {
      // eslint-disable-next-line no-console -- network diagnostics
      console.error("Failed to fetch facets", err);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchCountries();
    fetchFacets();
  }, [fetchCountries, fetchFacets]);

  useEffect(() => {
    if (addressCountry) fetchStates(addressCountry);
    else {
      setStates([]);
      setStatesFetchSettled(true);
    }
  }, [addressCountry, fetchStates]);

  useEffect(() => {
    if (!addressCountry) return;
    if (states.length === 0) return;
    if (!addressState) return;
    if (!states.some((s) => s.stateCode === addressState)) {
      setAddressState("");
      addressStateNameFallbackRef.current = "";
    }
  }, [addressCountry, states, addressState]);

  useEffect(() => {
    if (previewMode) return;
    if (registration?.partnerRegistrationFormStep !== 4) {
      dispatch(setPartnerRegistrationFormStep(4));
    }
  }, [dispatch, registration?.partnerRegistrationFormStep, previewMode]);

  const parentCompanyDisplayName = useCallback((item: any | null) => {
    if (!item) return "";
    return String(item.name ?? item.companyName ?? "").trim();
  }, []);

  const searchParentCompanies = useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      if (!trimmed) return;
      setParentCompanyLoading(true);
      try {
        const response: any = await getAffiliatedCompany(
          PROGRAM_SPECIFIC_NAMES[PROGRAM].programType,
          query,
        );
        if (trimmed !== parentCompanySearchRef.current.trim()) {
          return;
        }
        const selectedName = parentCompanyDisplayName(
          selectedParentCompanyRef.current,
        );
        if (selectedName && trimmed === selectedName) {
          setParentCompanySuggestions([]);
          setShowParentSuggestions(false);
          return;
        }
        if (response?.status === 1 && Array.isArray(response?.data)) {
          setParentCompanySuggestions(response.data);
          setShowParentSuggestions(true);
        } else {
          setParentCompanySuggestions([]);
          setShowParentSuggestions(false);
        }
      } catch {
        if (trimmed === parentCompanySearchRef.current.trim()) {
          setParentCompanySuggestions([]);
          setShowParentSuggestions(false);
        }
      } finally {
        setParentCompanyLoading(false);
      }
    },
    [parentCompanyDisplayName],
  );

  useEffect(() => {
    if (parentSearchDebounce.current) {
      clearTimeout(parentSearchDebounce.current);
    }
    const trimmed = parentCompanySearch.trim();
    if (!trimmed) {
      setParentCompanySuggestions([]);
      setShowParentSuggestions(false);
    } else {
      const selectedName = parentCompanyDisplayName(selectedParentCompany);
      if (selectedName && trimmed === selectedName) {
        setParentCompanySuggestions([]);
        setShowParentSuggestions(false);
      } else {
        parentSearchDebounce.current = setTimeout(() => {
          searchParentCompanies(parentCompanySearch);
        }, 300);
      }
    }
    return () => {
      if (parentSearchDebounce.current) {
        clearTimeout(parentSearchDebounce.current);
      }
    };
  }, [
    parentCompanySearch,
    selectedParentCompany,
    searchParentCompanies,
    parentCompanyDisplayName,
  ]);

  const selectParentCompanySuggestion = useCallback(
    (item: any) => {
      if (parentSearchDebounce.current) {
        clearTimeout(parentSearchDebounce.current);
        parentSearchDebounce.current = null;
      }
      setSelectedParentCompany(item);
      setParentCompanySearch(item.name ?? item.companyName ?? "");
      setParentCompanySuggestions([]);
      setShowParentSuggestions(false);
      markTouched("parentCompany");
    },
    [markTouched],
  );

  /** Mirrors CustomAddressChoiceRenderer: copy parent address into company fields or clear them. */
  const applyAddressChoice = useCallback(
    (choice: "Parent Address" | "Partner Address") => {
      const prev = addressChoiceRef.current;
      setAddressChoice(choice);
      if (choice === "Parent Address") {
        const addr = selectedParentCompany?.address;
        if (addr) {
          setHqAddress(String(addr.hqAddress ?? "").trim());
          setAddressCountry(addr.country ?? "");
          setAddressState(addr.state ?? "");
          setAddressCity(String(addr.city ?? "").trim());
          setAddressZipCode(String(addr.zipCode ?? "").trim());
        }
      } else if (prev === "Parent Address") {
        setHqAddress("");
        setAddressCountry("");
        setAddressState("");
        setAddressCity("");
        setAddressZipCode("");
      }
    },
    [selectedParentCompany],
  );

  const handleParentCompanySearchChange = useCallback(
    (value: string) => {
      setParentCompanySearch(value);
      const trimmed = value.trim();
      const clearParentSelection = () => {
        setSelectedParentCompany(null);
        if (addressChoiceRef.current === "Parent Address") {
          applyAddressChoice("Partner Address");
        }
      };
      if (!trimmed) {
        clearParentSelection();
        return;
      }
      if (selectedParentCompany) {
        const selectedName = String(
          selectedParentCompany.name ?? selectedParentCompany.companyName ?? "",
        ).trim();
        if (trimmed !== selectedName) {
          clearParentSelection();
        }
      }
    },
    [selectedParentCompany, applyAddressChoice],
  );

  useEffect(() => {
    if (!isSubsidiary) return;
    if (addressChoice !== "Parent Address" || !selectedParentCompany?.address) {
      return;
    }
    const a = selectedParentCompany.address;
    setHqAddress(String(a.hqAddress ?? "").trim());
    setAddressCountry(a.country ?? "");
    setAddressState(a.state ?? "");
    setAddressCity(String(a.city ?? "").trim());
    setAddressZipCode(String(a.zipCode ?? "").trim());
  }, [isSubsidiary, addressChoice, selectedParentCompany]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 640px)");
    const onChange = () => setNarrowForAddressChoice(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const showAddressFields =
    !isSubsidiary || addressChoice === "Partner Address";

  const parentAddressPreviewLines = useMemo((): {
    key: string;
    text: string;
  }[] => {
    if (!selectedParentCompany) return [];
    const rows: { key: string; text: string }[] = [];
    const displayName = String(
      selectedParentCompany.name ?? selectedParentCompany.companyName ?? "",
    ).trim();
    if (displayName) rows.push({ key: "name", text: displayName });
    const addr = selectedParentCompany.address as
      | {
          hqAddress?: string;
          city?: string;
          state?: string;
          stateName?: string;
          country?: string;
          countryName?: string;
          zipCode?: string;
        }
      | undefined;
    if (!addr) return rows;
    const hq = String(addr.hqAddress ?? "").trim();
    if (hq) rows.push({ key: "hq", text: hq });
    const city = String(addr.city ?? "").trim();
    const state =
      String(addr.stateName ?? "").trim() || String(addr.state ?? "").trim();
    const cc = String(addr.country ?? "").trim();
    const countryLabel =
      String(addr.countryName ?? "").trim() ||
      (cc && countries.find((c) => c.countrycode === cc)?.country) ||
      cc;
    const locality = [city, state, countryLabel].filter(Boolean).join(", ");
    if (locality) rows.push({ key: "locality", text: locality });
    const zip = String(addr.zipCode ?? "").trim();
    if (zip) rows.push({ key: "zip", text: zip });
    return rows;
  }, [selectedParentCompany, countries]);

  useEffect(() => {
    if (
      showAddressFields &&
      addressCountry &&
      !phoneDialCountryCode &&
      countries.some((c) => c.countrycode === addressCountry)
    ) {
      setPhoneDialCountryCode(addressCountry);
    }
  }, [addressCountry, countries, phoneDialCountryCode, showAddressFields]);

  const phonePickerCountries = useMemo(
    (): PhonePickerCountry[] =>
      countries.map((c) => ({ ...c, id: c.countrycode })),
    [countries],
  );

  const phoneDialPickerKey = useMemo((): string | null => {
    if (!phoneDialCountryCode) return null;
    return countries.some((c) => c.countrycode === phoneDialCountryCode)
      ? phoneDialCountryCode
      : null;
  }, [phoneDialCountryCode, countries]);

  const phoneDialPrefixDisplay = useMemo(() => {
    const c = countries.find((x) => x.countrycode === phoneDialCountryCode);
    return formatDialPrefixOnly(c?.phoneCode);
  }, [countries, phoneDialCountryCode]);

  const getPhoneFieldError = useCallback((): string | undefined => {
    if (!phoneDialCountryCode) {
      return "Select a country code for your phone number.";
    }
    if (!phone.trim()) {
      return "This field is required.";
    }
    if (!validateDxpPhoneLocalLine(phone.trim())) {
      return "Enter a valid phone number.";
    }
    return undefined;
  }, [phoneDialCountryCode, phone]);

  const computeValidationErrors = useCallback(
    (callFor: string): Record<string, string> => {
      const e: Record<string, string> = {};
      const cn = companyName.trim();
      if (!cn) {
        e.companyName = "This field is required.";
      } else if (cn.length > DXP_FIELD_MAX_LENGTH.companyInfo_companyName) {
        e.companyName = `Must be ${DXP_FIELD_MAX_LENGTH.companyInfo_companyName} characters or fewer.`;
      }

      const ws = website.trim();
      if (!ws) {
        e.website = "This field is required.";
      } else if (ws.length > DXP_FIELD_MAX_LENGTH.companyInfo_website) {
        e.website = `Must be ${DXP_FIELD_MAX_LENGTH.companyInfo_website} characters or fewer.`;
      } else if (!validateCompanyWebsiteUrl(ws)) {
        e.website = "Enter a valid website URL (e.g. https://www.example.com).";
      }

      const phoneErr = getPhoneFieldError();
      if (phoneErr) e.phone = phoneErr;

      const li = linkedin.trim();
      if (!li) {
        e.linkedin = "This field is required.";
      } else if (li.length > DXP_FIELD_MAX_LENGTH.companyInfo_linkedin) {
        e.linkedin = `Must be ${DXP_FIELD_MAX_LENGTH.companyInfo_linkedin} characters or fewer.`;
      } else if (!validateLinkedInProfileUrl(li)) {
        e.linkedin = DXP_LINKEDIN_URL_VALIDATION_MESSAGE;
      }

      const refErr = validateDxpReferredByOptional(referredBy);
      if (!refErr.valid) {
        e.referredBy = refErr.message;
      }

      if (!primaryBusiness) e.primaryBusiness = "This field is required.";
      if (!ownership) e.ownership = "This field is required.";
      if (!totalEmployees) {
        e.totalEmployees = "This field is required.";
      } else if (
        !(
          DXP_COMPANY_NUMBER_OF_EMPLOYEES_OPTIONS as readonly string[]
        ).includes(totalEmployees)
      ) {
        e.totalEmployees = "Please select a valid option.";
      }

      const descErr = getDxpAdditionalInfoFieldError(
        companyDescription,
        DXP_FIELD_MAX_LENGTH.companyInfo_companyDescription,
        { required: callFor === "callForNxt" },
      );
      if (descErr) e.companyDescription = descErr;

      const benErr = getDxpAdditionalInfoFieldError(
        programBenefits,
        DXP_FIELD_MAX_LENGTH.companyInfo_programBenefits,
        { required: callFor === "callForNxt" },
      );
      if (benErr) e.programBenefits = benErr;

      if (showAddressFields) {
        const hq = hqAddress.trim();
        if (!hq) {
          e.hqAddress = "This field is required.";
        } else if (
          hq.length > DXP_FIELD_MAX_LENGTH.companyInfo_address_hqAddress
        ) {
          e.hqAddress = `Must be ${DXP_FIELD_MAX_LENGTH.companyInfo_address_hqAddress} characters or fewer.`;
        }
        if (!addressCountry) e.addressCountry = "This field is required.";
        const city = addressCity.trim();
        if (!city) {
          e.addressCity = "This field is required.";
        } else if (
          city.length > DXP_FIELD_MAX_LENGTH.companyInfo_address_city
        ) {
          e.addressCity = `Must be ${DXP_FIELD_MAX_LENGTH.companyInfo_address_city} characters or fewer.`;
        }
        const zip = addressZipCode.trim();
        if (!zip) {
          e.addressZipCode = "This field is required.";
        } else if (
          zip.length > DXP_FIELD_MAX_LENGTH.companyInfo_address_zipCode
        ) {
          e.addressZipCode = `Must be ${DXP_FIELD_MAX_LENGTH.companyInfo_address_zipCode} characters or fewer.`;
        }
        if (addressCountry) {
          if (!statesFetchSettled) {
            e.addressState = "This field is required.";
          } else if (states.length > 0) {
            if (!states.some((s) => s.stateCode === addressState)) {
              e.addressState = "This field is required.";
            }
          }
        }
      }
      if (isSubsidiary && !selectedParentCompany) {
        e.parentCompany =
          "Please select a parent company from the search results.";
      }
      return e;
    },
    [
      companyName,
      website,
      phoneDialCountryCode,
      phone,
      linkedin,
      referredBy,
      primaryBusiness,
      ownership,
      totalEmployees,
      companyDescription,
      programBenefits,
      showAddressFields,
      hqAddress,
      addressCountry,
      addressCity,
      addressZipCode,
      statesFetchSettled,
      states,
      addressState,
      isSubsidiary,
      selectedParentCompany,
      countries,
      getPhoneFieldError,
    ],
  );

  const filterDisplayErrors = useCallback(
    (raw: Record<string, string>): Record<string, string> => {
      const touched = touchedFields as Record<string, boolean | undefined>;
      const hasValue = (key: string): boolean => {
        switch (key) {
          case "companyName":
            return companyName.trim() !== "";
          case "website":
            return website.trim() !== "";
          case "phone":
            /* Show phone errors only after local digits exist or field touched — not when only dial is auto-selected. */
            return phone.trim() !== "";
          case "linkedin":
            return linkedin.trim() !== "";
          case "primaryBusiness":
            return Boolean(primaryBusiness);
          case "ownership":
            return Boolean(ownership);
          case "totalEmployees":
            return Boolean(totalEmployees);
          case "referredBy":
            return referredBy.trim() !== "";
          case "companyDescription":
            return companyDescription.trim() !== "";
          case "programBenefits":
            return programBenefits.trim() !== "";
          case "hqAddress":
            return hqAddress.trim() !== "";
          case "addressCountry":
            return Boolean(addressCountry);
          case "addressState":
            return Boolean(addressState);
          case "addressCity":
            return addressCity.trim() !== "";
          case "addressZipCode":
            return addressZipCode.trim() !== "";
          case "parentCompany":
            return Boolean(selectedParentCompany);
          default:
            return false;
        }
      };
      return Object.fromEntries(
        Object.entries(raw).filter(([key]) => touched[key] || hasValue(key)),
      );
    },
    [
      touchedFields,
      companyName,
      website,
      phone,
      linkedin,
      primaryBusiness,
      ownership,
      totalEmployees,
      referredBy,
      companyDescription,
      programBenefits,
      hqAddress,
      addressCountry,
      addressState,
      addressCity,
      addressZipCode,
      selectedParentCompany,
    ],
  );

  useEffect(() => {
    const raw = computeValidationErrors("callForNxt");
    let next = filterDisplayErrors(raw);
    if (duplicateCompanyNameFromApi) {
      next = { ...next, companyName: DUPLICATE_COMPANY_NAME_MESSAGE };
    }
    setErrors(next);
  }, [
    computeValidationErrors,
    filterDisplayErrors,
    duplicateCompanyNameFromApi,
  ]);

  const isFormValidForNext = useMemo(
    () => Object.keys(computeValidationErrors("callForNxt")).length === 0,
    [computeValidationErrors],
  );

  const isFormValidForSaveAndExit = useMemo(
    () =>
      Object.keys(computeValidationErrors("callForSaveAndExit")).length === 0,
    [computeValidationErrors],
  );

  /** Block Save/Next until company + address (when shown) pass validation; preview stays clickable for QA toasts. */
  const companyStepActionsDisabled =
    isLoading || (!previewMode && !isFormValidForNext);

  const saveAndExitActionDisabled =
    isLoading || (!previewMode && !isFormValidForSaveAndExit);

  const validate = (callFor: string): boolean => {
    const e = computeValidationErrors(callFor);
    let display = filterDisplayErrors(e);
    if (duplicateCompanyNameFromApi) {
      display = { ...display, companyName: DUPLICATE_COMPANY_NAME_MESSAGE };
    }
    setErrors(display);
    return Object.keys(e).length === 0;
  };

  const buildFormData = (): Record<string, any> => {
    const resolveAddressStateName = (stateCode: string): string => {
      const code = String(stateCode ?? "").trim();
      if (!code) return "";
      const row = states.find((s) => s.stateCode === code);
      if (row?.stateName) return row.stateName;
      return addressStateNameFallbackRef.current;
    };

    const dialCountry = countries.find(
      (c) => c.countrycode === phoneDialCountryCode,
    );
    const phoneCode = dialCountry?.phoneCode || phoneDialCountryCode;
    const combinedPhone =
      phone.trim() && phoneCode ? `${phoneCode}-${phone.trim()}` : phone.trim();

    const data: Record<string, any> = {
      companyInfo_isSubsidiary: isSubsidiary ? "Yes" : "No",
      companyInfo_companyName: companyName.trim(),
      companyInfo_website: website.trim(),
      companyInfo_phone: combinedPhone,
      companyInfo_linkedin: linkedin.trim() || undefined,
      companyInfo_primaryBusiness: primaryBusiness || undefined,
      companyInfo_ownership: ownership || undefined,
      companyInfo_totalEmployees: totalEmployees || undefined,
      companyInfo_referredBy: referredBy.trim() || undefined,
      companyInfo_companyDescription: companyDescription.trim(),
      companyInfo_programBenefits: programBenefits.trim(),
    };
    if (isSubsidiary && selectedParentCompany) {
      data.affiliatedCompanyName = selectedParentCompany.name;
      data.companyInfo_affiliatedCompany_name = selectedParentCompany.name;
      data.companyInfo_affiliatedCompany_accountId =
        selectedParentCompany.accountId ?? selectedParentCompany.id;
      data.companyInfo_affiliatedCompany_level = selectedParentCompany.level;
      if (selectedParentCompany.address) {
        data.companyInfo_affiliatedCompany_hqAddress =
          selectedParentCompany.address.hqAddress;
        data.companyInfo_affiliatedCompany_country =
          selectedParentCompany.address.country;
        data.companyInfo_affiliatedCompany_countryName =
          selectedParentCompany.address.countryName;
        data.companyInfo_affiliatedCompany_state =
          selectedParentCompany.address.state;
        data.companyInfo_affiliatedCompany_stateName =
          selectedParentCompany.address.stateName;
        data.companyInfo_affiliatedCompany_city =
          selectedParentCompany.address.city;
        data.companyInfo_affiliatedCompany_zipCode =
          selectedParentCompany.address.zipCode;
      }
      data.companyInfo_addressChoice = addressChoice;
    }
    if (
      isSubsidiary &&
      addressChoice === "Parent Address" &&
      selectedParentCompany?.address
    ) {
      const a = selectedParentCompany.address as {
        hqAddress?: string;
        country?: string;
        state?: string;
        stateName?: string;
        city?: string;
        zipCode?: string;
      };
      data.companyInfo_address_hqAddress = String(a.hqAddress ?? "").trim();
      data.companyInfo_address_country = a.country ?? "";
      data.companyInfo_address_state = a.state ?? "";
      data.companyInfo_address_city = String(a.city ?? "").trim();
      data.companyInfo_address_zipCode = String(a.zipCode ?? "").trim();
      data.companyInfo_address_stateName =
        String(a.stateName ?? "").trim() ||
        resolveAddressStateName(String(a.state ?? ""));
    } else if (showAddressFields) {
      data.companyInfo_address_hqAddress = hqAddress.trim();
      data.companyInfo_address_country = addressCountry;
      data.companyInfo_address_state = addressState;
      data.companyInfo_address_city = addressCity.trim();
      data.companyInfo_address_zipCode = addressZipCode.trim();
      data.companyInfo_address_stateName =
        resolveAddressStateName(addressState);
    }
    return data;
  };

  const augmentFormDataForCompanyJsonValidation = useCallback(
    (formData: Record<string, any>): Record<string, any> => {
      if (
        showAddressFields &&
        addressCountry &&
        statesFetchSettled &&
        states.length === 0 &&
        formData.companyInfo_addressChoice !== "Parent Address"
      ) {
        return {
          ...formData,
          [COMPANY_INFO_ADDRESS_STATE_PICKLIST_EMPTY]: "true",
        };
      }
      return formData;
    },
    [showAddressFields, addressCountry, statesFetchSettled, states.length],
  );

  const submitForm = async (submitData: Record<string, any>) => {
    const data = JSON.parse(JSON.stringify(submitData));
    delete data.step;
    delete data.type;
    data.action = "register";
    data.companyInfo_isSubsidiary = data.companyInfo_isSubsidiary === "Yes";
    data.registrationType = "Primary";

    const payload: any = unflattenWithSeparator(data, "_");
    applyDxpPersonalInfoRegisterDefaults(payload);

    if (payload.companyInfo?.primaryBusiness) {
      const primaryBusinessMapping: Record<string, string> = {
        "Consulting/Professional Services": "Solution",
        "ISV/Data Provider": "Technology",
      };
      payload.companyInfo.primaryBusiness =
        primaryBusinessMapping[payload.companyInfo.primaryBusiness] ??
        payload.companyInfo.primaryBusiness;
    }

    if (data.companyInfo_companyName) {
      localStorage.setItem("companyName", data.companyInfo_companyName);
    }
    payload.companyInfo = payload.companyInfo || {};
    payload.companyInfo.redirectTo = "membershipdetails";

    if (accountId) payload.id = accountId;

    setIsLoading(true);
    try {
      const response: any = await saveRegistrationForm(
        PROGRAM_SPECIFIC_NAMES[PROGRAM].programType,
        payload,
      );
      if (response.status === 1) {
        if (response?.data?.errorCode === "10009") {
          setDuplicateCompanyNameFromApi(true);
          displayToasts({
            type: "error",
            text: DUPLICATE_COMPANY_NAME_MESSAGE,
            autoClose: true,
          });
        } else {
          sessionStorage.removeItem("registrationFormData");
          const accountStatus = localStorage.getItem("accountStatus");
          const companyNameForSuccess = localStorage.getItem("companyName");
          const membershipTypeForSuccess =
            getStoredMembershipType() || getCompanyPartnerLevel(response?.data);
          const selectedCompanyTypeForSuccess =
            registration.selectedRegisterCompanyType ||
            localStorage.getItem("selectedRegisterCompanyType");
          const affiliatedCompanyNameForSuccess =
            isSubsidiary && selectedParentCompany
              ? String(
                  selectedParentCompany.name ??
                    selectedParentCompany.companyName ??
                    "",
                ).trim()
              : "";
          const parentCompanyLevelForSuccess =
            isSubsidiary && selectedParentCompany
              ? getCompanyPartnerLevel(selectedParentCompany)
              : "";
          localStorage.clear();
          if (accountStatus)
            localStorage.setItem("accountStatus", accountStatus);
          if (accountId) localStorage.setItem("accountId", accountId);
          if (companyNameForSuccess)
            localStorage.setItem("companyName", companyNameForSuccess);
          if (membershipTypeForSuccess) {
            persistPartnerLevel({ level: membershipTypeForSuccess });
          }
          if (selectedCompanyTypeForSuccess)
            localStorage.setItem(
              "selectedRegisterCompanyType",
              selectedCompanyTypeForSuccess,
            );
          if (affiliatedCompanyNameForSuccess)
            localStorage.setItem(
              "affiliatedCompanyName",
              affiliatedCompanyNameForSuccess,
            );
          if (parentCompanyLevelForSuccess)
            localStorage.setItem(
              "parentCompanyLevel",
              parentCompanyLevelForSuccess,
            );
          navigate(PROGRAM_SPECIFIC_NAMES[PROGRAM].route.requestSubmitted);
        }
      } else {
        displayToasts({
          type: "error",
          text: response?.message ?? "Something went wrong please try again.",
          autoClose: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (previewMode) {
      displayToasts({
        type: "info",
        text: "Preview mode — back is disabled.",
        autoClose: true,
      });
      return;
    }

    const formData = buildFormData();
    const persistedStr = sessionStorage.getItem("registrationFormData");
    let persisted: Record<string, any> = {};
    try {
      if (persistedStr) persisted = JSON.parse(persistedStr);
    } catch {
      // eslint-disable-next-line no-console -- corrupt session data
      console.warn("Could not parse persisted registration data");
    }

    const merged = mergePersonalDraftIntoFlatForm(
      { ...persisted, ...formData },
      registration?.dxpUserProfileDraft,
    );
    delete merged.step;
    delete merged.type;
    sessionStorage.setItem("registrationFormData", JSON.stringify(merged));

    dispatch(setPartnerRegistrationFormStep(3));
    window.scrollTo(0, 0);
  };

  const handleNextOrRegister = async () => {
    if (companyStepActionsDisabled) return;
    clearCompanyValidationInlineErrors();
    const formData = buildFormData();
    let companyErrors: Array<{ instancePath: string; message: string }> = [];
    try {
      companyErrors =
        validateCompanyInfo(
          augmentFormDataForCompanyJsonValidation(formData),
        ) || [];
    } catch {
      companyErrors = [
        {
          instancePath: "/companyInfo_companyName",
          message: "Please fix the errors below.",
        },
      ];
    }
    if (companyErrors.length > 0 && hasAnyCompanyFieldInput(formData)) {
      scheduleShowCompanyValidationErrorsBelowFields(companyErrors);
      return;
    }
    if (!validate("callForNxt")) return;
    if (previewMode) {
      displayToasts({
        type: "info",
        text: "Preview mode — continue is disabled.",
        autoClose: true,
      });
      return;
    }

    const persistedStr = sessionStorage.getItem("registrationFormData");
    let persisted: Record<string, any> = {};
    try {
      if (persistedStr) persisted = JSON.parse(persistedStr);
    } catch {
      // eslint-disable-next-line no-console -- corrupt session data
      console.warn("Could not parse persisted registration data");
    }

    const merged = mergePersonalDraftIntoFlatForm(
      { ...persisted, ...formData },
      registration?.dxpUserProfileDraft,
    );
    delete merged.step;
    delete merged.type;

    if (isSubsidiary) {
      merged.action = "register";
      merged.type = "register";
      await submitForm(merged);
    } else {
      dispatch(setPartnerRegistrationFormStep(5));
      sessionStorage.setItem(
        "registrationFormData",
        JSON.stringify({ ...merged, step: "2" }),
      );
      window.scrollTo(0, 0);
    }
  };

  const handleSaveExit = async () => {
    if (saveAndExitActionDisabled && !previewMode) return;
    if (previewMode) {
      displayToasts({
        type: "info",
        text: "Preview mode — save is disabled.",
        autoClose: true,
      });
      return;
    }
    clearCompanyValidationInlineErrors();
    const formData = buildFormData();
    let companyErrors: Array<{ instancePath: string; message: string }> = [];
    try {
      companyErrors =
        validateCompanyInfoForSaveExit(
          augmentFormDataForCompanyJsonValidation(formData),
        ) || [];
    } catch {
      companyErrors = [
        {
          instancePath: "/companyInfo_companyName",
          message: "Please fix the errors below.",
        },
      ];
    }
    if (companyErrors.length > 0 && hasAnyCompanyFieldInput(formData)) {
      scheduleShowCompanyValidationErrorsBelowFields(companyErrors);
      return;
    }
    if (!validate("callForSaveAndExit")) return;

    const persistedStr = sessionStorage.getItem("registrationFormData");
    let persisted: Record<string, any> = {};
    try {
      if (persistedStr) persisted = JSON.parse(persistedStr);
    } catch {
      // eslint-disable-next-line no-console -- corrupt session data
      console.warn("Could not parse persisted registration data");
    }

    const merged: Record<string, any> = mergePersonalDraftIntoFlatForm(
      {
        ...persisted,
        ...buildFormData(),
        action: "save-exit",
      },
      registration?.dxpUserProfileDraft,
    );
    delete merged.step;
    delete merged.type;

    const data = JSON.parse(JSON.stringify(merged));
    delete data.affiliatedCompanyName;
    data.companyInfo_isSubsidiary = data.companyInfo_isSubsidiary === "Yes";
    data.registrationType = "Primary";

    const payload: any = unflattenWithSeparator(data, "_");
    applyDxpPersonalInfoRegisterDefaults(payload);

    if (payload.companyInfo?.primaryBusiness) {
      const primaryBusinessMapping: Record<string, string> = {
        "Consulting/Professional Services": "Solution",
        "ISV/Data Provider": "Technology",
      };
      payload.companyInfo.primaryBusiness =
        primaryBusinessMapping[payload.companyInfo.primaryBusiness] ??
        payload.companyInfo.primaryBusiness;
    }

    payload.companyInfo = payload.companyInfo || {};
    payload.companyInfo.redirectTo = "membershipdetails";
    if (payload.companyInfo.companyDescription == null) {
      payload.companyInfo.companyDescription = "";
    }
    if (payload.companyInfo.programBenefits == null) {
      payload.companyInfo.programBenefits = "";
    }

    if (accountId) payload.id = accountId;

    setIsLoading(true);
    try {
      const response: any = await saveRegistrationForm(
        PROGRAM_SPECIFIC_NAMES[PROGRAM].programType,
        payload,
      );
      if (response.status === 1) {
        if (response?.data?.errorCode === "10009") {
          setDuplicateCompanyNameFromApi(true);
          displayToasts({
            type: "error",
            text: DUPLICATE_COMPANY_NAME_MESSAGE,
            autoClose: true,
          });
        } else {
          const registerData = response?.data as {
            accId?: string;
            companyInfo?: { id?: string };
            id?: string;
          };
          const savedPartnerId =
            registerData?.accId ||
            registerData?.companyInfo?.id ||
            registerData?.id;
          if (savedPartnerId)
            localStorage.setItem("accountId", String(savedPartnerId));
          localStorage.setItem("redirectTo", "membershipdetails");
          localStorage.setItem("partnerRegistrationFormStep", "4");
          navigate(PROGRAM_SPECIFIC_NAMES[PROGRAM].route.saveSuccess);
        }
      } else {
        displayToasts({
          type: "error",
          text: response?.message ?? "Something went wrong please try again.",
          autoClose: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const primaryBusinessOptions = [
    "Consulting/Professional Services",
    "ISV/Data Provider",
  ];

  const addressChoiceRadioClass = (isActive: boolean) =>
    [
      "dxp-s2-radio-card",
      "dxp-s2-radio-card--max-350",
      narrowForAddressChoice
        ? "dxp-s2-radio-card--stacked-full"
        : "dxp-s2-radio-card--grow",
      isActive ? "dxp-s2-radio-card--active" : "dxp-s2-radio-card--inactive",
    ].join(" ");

  const stepTotal = selectedRegisterCompanyType === "subsidaryCompany" ? 4 : 5;

  return (
    <div className="dxp-registration-page-shell registration-page-wrapper">
      <DxpRegistrationTopHeader program={PROGRAM} />
      <div className="dxp-registration-content-row registration-content-wrapper">
        <WizardNavLayout
          previewStepOverride={previewMode ? 4 : undefined}
          previewCompanyTypeOverride={
            previewMode ? "standaloneCompany" : undefined
          }
        />
        <View UNSAFE_className="dxp-wizard-main-column">
          <View UNSAFE_className="dxp-wizard-step-panel dxp-co-step-panel-stack">
            <Flex
              direction="row"
              justifyContent="space-between"
              alignItems="start"
              UNSAFE_className="dxp-wizard-step-heading-row dxp-wizard-flex-heading-row"
            >
              <Text UNSAFE_className="dxp-wizard-step-title-text">
                {companyInfoConfig.title}
              </Text>
              <Text UNSAFE_className="dxp-wizard-step-meta-label">
                {`STEP 4/${stepTotal}`}
              </Text>
            </Flex>
            <div className="dxp-co-title-rule" aria-hidden />

            {isSubsidiary && (
              <View UNSAFE_className="dxp-mb-24">
                <h2 className="dxp-co-section-heading-title dxp-co-section-heading">
                  {companyInfoConfig.parentCompanyTitle}{" "}
                  <span className="dxp-required-marker">*</span>
                </h2>
                <div className="dxp-co-suggest-anchor">
                  <SearchField
                    value={parentCompanySearch}
                    onChange={handleParentCompanySearchChange}
                    placeholder="Search for your parent company"
                    aria-label="Parent company search"
                    UNSAFE_className="dxp-w-full"
                    isInvalid={!!errors.parentCompany}
                    onBlur={() => {
                      markTouched("parentCompany");
                      setTimeout(() => setShowParentSuggestions(false), 200);
                    }}
                    onFocus={() =>
                      parentCompanySuggestions.length > 0 &&
                      setShowParentSuggestions(true)
                    }
                  />
                  {parentCompanyLoading ? (
                    <div className="dxp-co-suggest-spinner">
                      <ProgressCircle size="S" isIndeterminate />
                    </div>
                  ) : null}
                  {showParentSuggestions &&
                    parentCompanySuggestions.length > 0 && (
                      <ul className="dxp-co-suggest-list">
                        {parentCompanySuggestions.map(
                          (item: any, idx: number) => (
                            <li
                              key={item.id ?? item.accountId ?? idx}
                              className="dxp-co-suggest-item"
                              role="option"
                              aria-selected={
                                selectedParentCompany?.id === item.id ||
                                selectedParentCompany?.accountId ===
                                  item.accountId
                              }
                              tabIndex={0}
                              onPointerDown={(e) => {
                                // Prevent SearchField blur so the list stays mounted until selection runs.
                                e.preventDefault();
                                selectParentCompanySuggestion(item);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  selectParentCompanySuggestion(item);
                                }
                              }}
                            >
                              {item.name ?? item.companyName ?? ""}
                            </li>
                          ),
                        )}
                      </ul>
                    )}
                </div>
                {errors.parentCompany ? (
                  <Text UNSAFE_className="dxp-co-error-text">
                    {errors.parentCompany}
                  </Text>
                ) : null}
              </View>
            )}

            <View UNSAFE_className="dxp-mb-24">
              <TextField
                label={companyInfoConfig.fieldLabels.companyName}
                labelAlign="start"
                isRequired
                necessityIndicator="icon"
                maxLength={DXP_FIELD_MAX_LENGTH.companyInfo_companyName}
                value={companyName}
                onChange={(v) => {
                  setDuplicateCompanyNameFromApi(false);
                  setCompanyName(v);
                }}
                onBlur={() => markTouched("companyName")}
                isInvalid={!!errors.companyName}
                errorMessage={errors.companyName}
                UNSAFE_className="dxp-w-full text-field"
              />
            </View>
            <View UNSAFE_className="dxp-mb-24">
              <TextField
                label={companyInfoConfig.fieldLabels.website}
                labelAlign="start"
                isRequired
                necessityIndicator="icon"
                maxLength={DXP_FIELD_MAX_LENGTH.companyInfo_website}
                placeholder="https://"
                value={website}
                onChange={setWebsite}
                onBlur={() => markTouched("website")}
                isInvalid={!!errors.website}
                errorMessage={errors.website}
                UNSAFE_className="dxp-w-full text-field"
              />
            </View>
            <View UNSAFE_className="dxp-mb-24 dxp-w-full">
              <Text UNSAFE_className="dxp-co-faux-field-label">
                {companyInfoConfig.fieldLabels.phone}
                <span className="dxp-required-marker--alt"> *</span>
              </Text>
              <DxpPhoneNumberField
                countrySelector={
                  <Picker
                    aria-label="Phone country code"
                    placeholder="Select"
                    size="M"
                    items={phonePickerCountries}
                    selectedKey={phoneDialPickerKey}
                    onSelectionChange={(key) => {
                      if (key == null) return;
                      markTouched("phone");
                      setPhoneDialCountryCode(String(key));
                    }}
                    isInvalid={!!errors.phone}
                    isDisabled={countries.length === 0}
                    UNSAFE_className="dxp-w-full drop-field"
                  >
                    {(item: PhonePickerCountry) => {
                      const dial = formatPhoneDialDisplay(
                        item.phoneCode,
                        item.countrycode,
                      );
                      return (
                        <PickerItem
                          id={item.id}
                          textValue={`${item.countrycode} ${item.country} ${dial}`}
                        >
                          {item.countrycode}
                        </PickerItem>
                      );
                    }}
                  </Picker>
                }
                dialPrefix={phoneDialPrefixDisplay || undefined}
                value={phone}
                onChange={(v) => {
                  markTouched("phone");
                  setPhone(v);
                }}
                placeholder="Enter phone number"
                aria-label="Company phone number"
                isInvalid={!!errors.phone}
                maxLength={20}
                size="M"
                pickerColumnWidth={80}
                UNSAFE_className="dxp-w-full"
              />
              {errors.phone ? (
                <Text UNSAFE_className="dxp-co-error-text">{errors.phone}</Text>
              ) : null}
            </View>
            <View UNSAFE_className="dxp-mb-24">
              <TextField
                label={companyInfoConfig.fieldLabels.linkedin}
                labelAlign="start"
                isRequired
                necessityIndicator="icon"
                maxLength={DXP_FIELD_MAX_LENGTH.companyInfo_linkedin}
                placeholder="https://"
                value={linkedin}
                onChange={setLinkedin}
                onBlur={() => markTouched("linkedin")}
                isInvalid={!!errors.linkedin}
                errorMessage={errors.linkedin}
                UNSAFE_className="dxp-w-full text-field"
              />
            </View>
            <View UNSAFE_className="dxp-mb-24">
              <Picker
                label={companyInfoConfig.fieldLabels.primaryBusiness}
                labelAlign="start"
                isRequired
                necessityIndicator="icon"
                placeholder="Select"
                selectedKey={primaryBusiness || null}
                onSelectionChange={(k) => {
                  markTouched("primaryBusiness");
                  setPrimaryBusiness((k as string) ?? "");
                }}
                isInvalid={!!errors.primaryBusiness}
                errorMessage={errors.primaryBusiness}
                UNSAFE_className="dxp-w-full drop-field"
              >
                {primaryBusinessOptions.map((opt) => (
                  <PickerItem key={opt} id={opt} textValue={opt}>
                    {opt}
                  </PickerItem>
                ))}
              </Picker>
            </View>
            <View UNSAFE_className="dxp-mb-24">
              <Picker
                label={companyInfoConfig.fieldLabels.ownership}
                labelAlign="start"
                isRequired
                necessityIndicator="icon"
                placeholder="Select"
                selectedKey={ownership || null}
                onSelectionChange={(k) => {
                  markTouched("ownership");
                  setOwnership((k as string) ?? "");
                }}
                isInvalid={!!errors.ownership}
                errorMessage={errors.ownership}
                UNSAFE_className="dxp-w-full drop-field"
              >
                {(facets.ownership ?? []).map((opt: string) => (
                  <PickerItem key={opt} id={opt} textValue={opt}>
                    {opt}
                  </PickerItem>
                ))}
              </Picker>
            </View>
            <View UNSAFE_className="dxp-mb-24">
              <Picker
                label={companyInfoConfig.fieldLabels.totalEmployees}
                labelAlign="start"
                isRequired
                necessityIndicator="icon"
                placeholder="Select"
                selectedKey={totalEmployees || null}
                onSelectionChange={(k) => {
                  markTouched("totalEmployees");
                  setTotalEmployees((k as string) ?? "");
                }}
                isInvalid={!!errors.totalEmployees}
                errorMessage={errors.totalEmployees}
                UNSAFE_className="dxp-w-full drop-field"
              >
                {DXP_COMPANY_NUMBER_OF_EMPLOYEES_OPTIONS.map((opt) => (
                  <PickerItem key={opt} id={opt} textValue={opt}>
                    {opt}
                  </PickerItem>
                ))}
              </Picker>
            </View>
            <View UNSAFE_className="dxp-mb-24">
              <TextField
                label={companyInfoConfig.fieldLabels.referredBy}
                labelAlign="start"
                maxLength={DXP_FIELD_MAX_LENGTH.companyInfo_referredBy}
                value={referredBy}
                onChange={setReferredBy}
                onBlur={() => markTouched("referredBy")}
                isInvalid={!!errors.referredBy}
                errorMessage={errors.referredBy}
                UNSAFE_className="dxp-w-full text-field"
              />
            </View>

            {isSubsidiary && (
              <View UNSAFE_className="dxp-mb-24">
                <h2 className="dxp-co-section-heading-title dxp-co-section-heading dxp-co-address-choice-heading">
                  {companyInfoConfig.yourCompanyAddressTitle}
                </h2>
                <RadioGroup
                  value={addressChoice}
                  onChange={(v) =>
                    applyAddressChoice(
                      v as "Parent Address" | "Partner Address",
                    )
                  }
                  orientation={
                    narrowForAddressChoice ? "vertical" : "horizontal"
                  }
                  aria-label={companyInfoConfig.yourCompanyAddressTitle}
                  UNSAFE_className="dxp-company-type-radio-group dxp-co-address-choice-radio-group dxp-w-full"
                >
                  <Radio
                    value="Parent Address"
                    isDisabled={!selectedParentCompany?.address}
                    UNSAFE_className={addressChoiceRadioClass(
                      addressChoice === "Parent Address",
                    )}
                  >
                    <Flex direction="column" gap={10}>
                      <Text UNSAFE_className="dxp-company-type-heading">
                        {companyInfoConfig.useParentCompanyAddressOption}
                      </Text>
                      {selectedParentCompany ? (
                        <div className="dxp-co-parent-address-block">
                          {parentAddressPreviewLines.length > 0 ? (
                            parentAddressPreviewLines.map(({ key, text }) => (
                              <Text
                                key={key}
                                UNSAFE_className="dxp-co-parent-address-line"
                              >
                                {text}
                              </Text>
                            ))
                          ) : (
                            <Text UNSAFE_className="dxp-co-parent-address-placeholder">
                              {
                                companyInfoConfig.parentAddressPreviewPlaceholder
                              }
                            </Text>
                          )}
                        </div>
                      ) : (
                        <Text UNSAFE_className="dxp-co-parent-address-placeholder">
                          {companyInfoConfig.parentAddressPreviewPlaceholder}
                        </Text>
                      )}
                    </Flex>
                  </Radio>
                  <Radio
                    value="Partner Address"
                    UNSAFE_className={addressChoiceRadioClass(
                      addressChoice === "Partner Address",
                    )}
                  >
                    <Flex direction="column" gap={8}>
                      <Text UNSAFE_className="dxp-company-type-heading">
                        {
                          companyInfoConfig.registerDifferentSubsidiaryAddressOption
                        }
                      </Text>
                    </Flex>
                  </Radio>
                </RadioGroup>
              </View>
            )}

            {showAddressFields && !isSubsidiary && (
              <h2 className="dxp-co-section-heading-title dxp-co-section-heading dxp-mt-8-only">
                {companyInfoConfig.companyAddressSectionTitle}
              </h2>
            )}

            {showAddressFields && (
              <>
                <View
                  UNSAFE_className={
                    isSubsidiary
                      ? "dxp-mb-24 dxp-co-subsidiary-address-fields-top"
                      : "dxp-mb-24"
                  }
                >
                  <TextField
                    label={companyInfoConfig.fieldLabels.hqAddress}
                    labelAlign="start"
                    isRequired
                    necessityIndicator="icon"
                    maxLength={
                      DXP_FIELD_MAX_LENGTH.companyInfo_address_hqAddress
                    }
                    value={hqAddress}
                    onChange={setHqAddress}
                    onBlur={() => markTouched("hqAddress")}
                    isInvalid={!!errors.hqAddress}
                    errorMessage={errors.hqAddress}
                    UNSAFE_className="dxp-w-full text-field"
                  />
                </View>
                <View UNSAFE_className="dxp-mb-24">
                  <Picker
                    label={companyInfoConfig.fieldLabels.country}
                    labelAlign="start"
                    isRequired
                    necessityIndicator="icon"
                    placeholder="Select"
                    selectedKey={addressCountry || null}
                    onSelectionChange={(k) => {
                      markTouched("addressCountry");
                      setAddressCountry((k as string) ?? "");
                      setAddressState("");
                      addressStateNameFallbackRef.current = "";
                      setStatesFetchSettled(false);
                      setStates([]);
                    }}
                    isInvalid={!!errors.addressCountry}
                    errorMessage={errors.addressCountry}
                    UNSAFE_className="dxp-w-full drop-field"
                  >
                    {countries.map((c) => (
                      <PickerItem
                        key={c.countrycode}
                        id={c.countrycode}
                        textValue={c.country}
                      >
                        {c.country}
                      </PickerItem>
                    ))}
                  </Picker>
                </View>
                <View UNSAFE_className="dxp-mb-24">
                  <Picker
                    label={companyInfoConfig.fieldLabels.state}
                    labelAlign="start"
                    isRequired
                    necessityIndicator="icon"
                    placeholder="Select"
                    selectedKey={addressState || null}
                    onSelectionChange={(k) => {
                      markTouched("addressState");
                      const code = (k as string) ?? "";
                      setAddressState(code);
                      const row = states.find((s) => s.stateCode === code);
                      addressStateNameFallbackRef.current =
                        row?.stateName ?? "";
                    }}
                    isInvalid={!!errors.addressState}
                    errorMessage={errors.addressState}
                    isDisabled={!addressCountry}
                    UNSAFE_className="dxp-w-full drop-field"
                  >
                    {states.map((s) => (
                      <PickerItem
                        key={s.stateCode}
                        id={s.stateCode}
                        textValue={s.stateName}
                      >
                        {s.stateName}
                      </PickerItem>
                    ))}
                  </Picker>
                </View>
                <View UNSAFE_className="dxp-mb-24">
                  <TextField
                    label={companyInfoConfig.fieldLabels.city}
                    labelAlign="start"
                    isRequired
                    necessityIndicator="icon"
                    maxLength={DXP_FIELD_MAX_LENGTH.companyInfo_address_city}
                    value={addressCity}
                    onChange={setAddressCity}
                    onBlur={() => markTouched("addressCity")}
                    isInvalid={!!errors.addressCity}
                    errorMessage={errors.addressCity}
                    UNSAFE_className="dxp-w-full text-field"
                  />
                </View>
                <View UNSAFE_className="dxp-mb-24">
                  <TextField
                    label={companyInfoConfig.fieldLabels.zipCode}
                    labelAlign="start"
                    isRequired
                    necessityIndicator="icon"
                    maxLength={DXP_FIELD_MAX_LENGTH.companyInfo_address_zipCode}
                    value={addressZipCode}
                    onChange={setAddressZipCode}
                    onBlur={() => markTouched("addressZipCode")}
                    isInvalid={!!errors.addressZipCode}
                    errorMessage={errors.addressZipCode}
                    UNSAFE_className="dxp-w-full text-field"
                  />
                </View>
              </>
            )}

            <h2 className="dxp-co-section-heading-title dxp-co-section-heading dxp-mt-28">
              {companyInfoConfig.additionalInfoTitle}
            </h2>
            <View UNSAFE_className="dxp-mb-8">
              <TextArea
                label={companyInfoConfig.fieldLabels.companyDescription}
                labelAlign="start"
                isRequired
                necessityIndicator="icon"
                maxLength={DXP_FIELD_MAX_LENGTH.companyInfo_companyDescription}
                value={companyDescription}
                onChange={setCompanyDescription}
                onBlur={() => markTouched("companyDescription")}
                isInvalid={!!errors.companyDescription}
                errorMessage={errors.companyDescription}
                UNSAFE_className="dxp-w-full"
              />
            </View>
            <Text UNSAFE_className="dxp-co-description-hint">
              {companyInfoConfig.companyDescriptionHint}
            </Text>
            <View UNSAFE_className="dxp-mb-24">
              <TextArea
                label={companyInfoConfig.fieldLabels.programBenefits}
                labelAlign="start"
                isRequired
                necessityIndicator="icon"
                maxLength={DXP_FIELD_MAX_LENGTH.companyInfo_programBenefits}
                value={programBenefits}
                onChange={setProgramBenefits}
                onBlur={() => markTouched("programBenefits")}
                isInvalid={!!errors.programBenefits}
                errorMessage={errors.programBenefits}
                UNSAFE_className="dxp-w-full"
              />
            </View>

            <Flex
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              UNSAFE_className="dxp-wizard-footer-actions dxp-wizard-footer-actions--split dxp-co-footer-shell"
            >
              <Flex
                direction="row"
                gap="size-200"
                alignItems="center"
                UNSAFE_className="dxp-wizard-footer-actions__end"
              >
                <Button
                  variant="secondary"
                  onPress={handleBack}
                  UNSAFE_className={secondaryFooterPillLabelClass}
                >
                  Back
                </Button>

                <Button
                  variant="secondary"
                  isDisabled={saveAndExitActionDisabled}
                  onPress={handleSaveExit}
                  UNSAFE_className={
                    saveAndExitActionDisabled
                      ? `${primaryFooterPillLabelClass} ${companyStepFooterActionDisabledClass}`
                      : primaryFooterPillLabelClass
                  }
                >
                  Save and exit
                </Button>
              </Flex>

              <Button
                variant="accent"
                isDisabled={companyStepActionsDisabled}
                onPress={handleNextOrRegister}
                UNSAFE_className={
                  companyStepActionsDisabled
                    ? `${primaryFooterPillLabelClass} ${companyStepFooterActionDisabledClass}`
                    : primaryFooterPillLabelClass
                }
              >
                {isSubsidiary ? "Register" : "Next"}
              </Button>
            </Flex>
          </View>
        </View>
      </div>
      {isLoading && <FullScreenLoader />}
    </div>
  );
}

CompanyInformation.defaultProps = {
  previewMode: false,
};

export default CompanyInformation;
